import _ from "lodash";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import FileViewer from "UIComponents/FileViewer";
import I18nHOC from "UIComponents/I18nHOC";
import {
  GetTheatreModeType,
  WORKBOOK_MODE_TYPES,
  PDFTRON_VIEWER_MODE_TYPES,
} from "Utils";
import { getAttachmentType } from "Utils/GetTheatreModeType";
import Workbook from "AppComponents/Workbook";
import { PdfTronViewerV3 } from "AppComponents/PdfTronViewer";
import ImageViewerV2 from "../../ImageViewerV2";
import classes from "./ViewerComponent.scss";
import GrouppedAssessmentToolsViewer from "./AssessmentToolViewer";
import GrouppedAssessmentToolsViewerV2 from "./AssessmentToolViewerV2";
import { Button, EmptyState } from "@toddle-edu/ds-web";
import { ArrowOutOutlined } from "@toddle-edu/ds-icons";
import { PostRateonLearningGoals } from "Post";
import { EvidenceIllustration } from "@toddle-edu/ds-theme";
import ACLStore from "lib/aclStore";
import {
  isPublicPlatformPostRoute,
  isPublicPlatformRoute,
} from "Login/modules/LoginModule";
import { APPLE_OFFICE_SUPPORTED_MIMETYPES, colors } from "Constants";
import { getStudentAssignmentMicrosoftLink } from "ClassRoom/modules/ClassRoomModule";
import { isMobileOrTablet } from "Utils/DeviceDetectHelpers";
import {
  getIsYoutubeLink,
  getShowYtLinkInTheatreMode,
  isNewNotesUi,
  isPlatformPostLinkRoute,
} from "Post/utils";
import ToddleDoc from "AppComponents/ToddleDoc";
import AITutorCodeEditorModality from "AppComponents/AIComponents/AITutorCodeEditorModality";

const VIEW = "view";

// Main viewer component
const ViewerComponent = React.memo(props => {
  const {
    mediaAttachments,
    setShowGiveFeedbackModal,
    shouldShowGiveFeedbackButton,
    onWorkbookUpdateSuccessfully,
    parentRef,
    iframeIdSelector,
    onPdftronUpdateSuccessfullly,
    userInfo,
    onDiscardDirectly,
    studentDetails,
    invokeWorkbookCustomLoader,
    onPdfTronSwitchToEditMode,
    preLoadPdfTron,
    activeIndex = 0,
    workbookMode = WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_VIEW"],
    imageViewerRef,
    videosPlaybackDetails,
    audiosPlaybackDetails,
    videoPlayerRef,
    audioPlayerRef,
    showSidePane,
    openLinkInNewTab,
    shouldFocusWorkbook = false,
    pdftronViewerMode = PDFTRON_VIEWER_MODE_TYPES["VIEWER"],
    t,
    setShowAttachmentEditor,
    postRatedLearningGoalsDetails,
    taggedStudents,
    childId,
    fullScreenButtonStatus,
    showSaveButton,
    xfdfString,
    setPdftronInstance,
    studentSubmissionId,
    getStudentAssignmentMicrosoftLink,
    testId,
    assignmentId,
    assessmentId,
    showRevampedEvaluationUI = false,
    isWorksheet = false,
    isJournal = false,
  } = props;

  const isNewNoteUi = isNewNotesUi();
  const isMobile = isMobileOrTablet();
  const [refLoaded, setRefLoaded] = useState(false);
  useEffect(() => {
    if (!_.isNull(parentRef?.current)) setRefLoaded(true);
  }, [parentRef]);
  const styles = useMemo(
    () => ({
      mediaPlaceHolderContainerStyle: {
        alignItems: "center",
        width: "100%",
        padding: isMobile ? undefined : "0 16px",
      },
      noteContainerStyle: {
        height: "unset",
        ...(!isNewNoteUi && { minHeight: "300px" }),
        margin: isMobile ? undefined : "16px 0",
        maxWidth: "536px",
        overflowY: "auto",
        padding: isMobile ? 0 : undefined,
      },
      noteLinesContainerStyles: {
        height: "unset",
      },
      noteContainerWithLimitStyles: {
        height: "unset",
        maxHeight: "unset",
        WebkitBoxOrient: "unset",
        minHeight: isNewNoteUi ? "80px" : "300px",
        display: "flex",
        flexDirection: "column",
      },
      itemStyles: {
        backgroundSize: "contain",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      },
      mediaPlaceHolderNoteContainerStyle: {
        alignItems: "center",
        width: isMobile ? "100%" : "40vw",
        padding: isMobile ? undefined : "0 16px",
        background: "none",
        borderRadius: "8px",
      },
      mediaPlaceHolderLinkStyle: {
        backgroundColor: colors.strokeOne,
        height: "176px",
        width: "176px",
        borderRadius: "8px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      mediaPlaceHolderItemStyle: {
        width: "176px",
        height: "176px",
        borderRadius: "8px",
      },
    }),
    [isMobile]
  );
  const mediaDataAttachments = _.get(mediaAttachments, "attachments");
  const assessmentTools = _.map(_.get(props, "assessmentTools", []));
  const isRateOnLearningGoalsPresent =
    _.size(_.get(props, "postRatedLearningGoalsDetails.elementPYP", [])) > 0;
  const items = [...mediaDataAttachments, ...assessmentTools];
  if (isRateOnLearningGoalsPresent) {
    items.push({
      id: _.get(props, "postRatedLearningGoalsDetails.elementPYP[0].id", ""),
      type: "POST_RATED_LEARNING_GOALS",
    });
  }
  const userType = _.get(props, "userInfo.user_type", "");
  const userId = _.get(props, "userInfo.id", "");
  const ratedStudents =
    userType == "parent" || userType == "student"
      ? [
          _.find(
            taggedStudents || [],
            student =>
              _.get(student, "node.id", "") ==
              (userType == "parent" ? childId : userId)
          ),
        ]
      : [...(taggedStudents || [])];

  const mediaDataAttachment = _.get(items, activeIndex) || mediaAttachments;
  let theatreModeType = "";
  if (activeIndex < _.size(mediaDataAttachments, 0))
    theatreModeType = GetTheatreModeType({
      media: mediaDataAttachment,
      isWorksheet,
    });
  else
    theatreModeType = !_.isNil(_.get(mediaDataAttachment, "assessmentToolType"))
      ? "ASSESSMENT_EVALUATION_TOOL_VIEWER"
      : isRateOnLearningGoalsPresent
      ? "POST_RATED_LEARNING_GOALS"
      : "";

  const attachmentTypeByMimeType =
    theatreModeType == "ASSESSMENT_EVALUATION_TOOL_VIEWER"
      ? ""
      : getAttachmentType(mediaDataAttachment);

  let showOpenInNewTabButton = false;
  const isAppleDocumentFile = _.includes(
    APPLE_OFFICE_SUPPORTED_MIMETYPES,
    _.get(mediaDataAttachment, "mimeType")
  );

  if (_.get(mediaDataAttachment, "type", "") == "LINK") {
    if (
      _.includes(
        ["PDF", "IMAGE", "VIDEO", "AUDIO", "FILE"],
        attachmentTypeByMimeType
      ) ||
      theatreModeType != "FILEVIEWER"
    ) {
      showOpenInNewTabButton = true;
    }
    if (
      getIsYoutubeLink(_.get(mediaDataAttachment, "url")) &&
      getShowYtLinkInTheatreMode()
    ) {
      showOpenInNewTabButton = false;
    }
  }
  if (
    _.get(mediaDataAttachment, "type", "") == "FILE" &&
    (isAppleDocumentFile || theatreModeType == "MEDIAPLACEHOLDER")
  ) {
    showOpenInNewTabButton = true;
  }

  const getMicrosoftDocumentLink = useCallback(
    async ({ attachmentId, submissionId, attachmentUrl }) => {
      const redirectUrl =
        attachmentId && submissionId
          ? await getStudentAssignmentMicrosoftLink({
              attachmentId,
              submissionId,
            })
          : null;

      if (attachmentUrl || redirectUrl)
        openLinkInNewTab({ url: redirectUrl ? redirectUrl : attachmentUrl });
    },
    []
  );

  const onClickToOpenNewTab = () => {
    const isMicrosoftTemplate =
      _.get(mediaDataAttachment, "metadata.serviceType", "") == "MICROSOFT" &&
      _.has(mediaDataAttachment, "metadata.permission");

    const attachmentUrl = isMicrosoftTemplate
      ? !_.isNil(_.get(mediaDataAttachment, "metadata.viewUrl"))
        ? _.get(mediaDataAttachment, "metadata.viewUrl")
        : _.get(props, "userInfo.user_type", "") === "staff"
        ? _.get(mediaDataAttachment, "url")
        : undefined
      : _.get(mediaDataAttachment, "url");

    if (
      ACLStore.can("FeatureFlag:MicrosoftAnyoneLinkGeneration") &&
      isMicrosoftTemplate
    ) {
      const attachmentId = _.get(mediaDataAttachment, ["id"]);
      getMicrosoftDocumentLink({
        attachmentId,
        submissionId: studentSubmissionId,
        attachmentUrl,
      });
      return;
    }
    if (attachmentUrl) openLinkInNewTab({ url: attachmentUrl });
  };

  const currentAudioPlaybackTime =
    _.get(mediaDataAttachment, "type", "FILE") == "AUDIO"
      ? _.get(
          _.find(
            audiosPlaybackDetails,
            audio => audio?.id == mediaDataAttachment?.id
          ),
          "timestamp",
          0
        )
      : 0;
  const currentVideoPlaybackTime =
    _.get(mediaDataAttachment, "type", "FILE") == "VIDEO"
      ? _.get(
          _.find(
            videosPlaybackDetails,
            video => video?.id == mediaDataAttachment?.id
          ),
          "timestamp",
          0
        )
      : 0;
  const isLinkType = _.get(mediaDataAttachment, "type") === "LINK";
  const isNonJournalRoute =
    !isJournal &&
    !isPlatformPostLinkRoute() &&
    !isPublicPlatformRoute() &&
    !isPublicPlatformPostRoute();

  switch (theatreModeType) {
    case "CODE_EDITOR_VIEWER":
      return (
        <AITutorCodeEditorModality
          attachmentId={
            _.get(mediaDataAttachment, "attachmentId") ??
            _.get(mediaDataAttachment, "id")
          }
          onClose={() => {}}
          mode={"view"}
        />
      );
    case "TODDLE_DOC_VIEWER":
      return (
        <ToddleDoc
          mode={VIEW}
          isCommentView={isNonJournalRoute}
          attachment={mediaDataAttachment}
        />
      );
    case "WORKBOOKVIEWER":
      return refLoaded ? (
        <Workbook
          attachment={mediaDataAttachment}
          workbookMode={WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_VIEW"]}
          isFullScreen={false}
          onUpdateSuccessfully={onWorkbookUpdateSuccessfully}
          setShowGiveFeedbackModal={setShowGiveFeedbackModal}
          shouldShowGiveFeedbackButton={shouldShowGiveFeedbackButton}
          parentRef={parentRef?.current}
          iframeIdSelector={iframeIdSelector}
          invokeWorkbookCustomLoader={invokeWorkbookCustomLoader}
          disableArr={["EXIT_BTN"]}
          shouldFocusWorkbook={shouldFocusWorkbook}
          showSeparateLoader={true}
          usePublicUrl={
            isPublicPlatformRoute() ||
            isPublicPlatformPostRoute() ||
            isPlatformPostLinkRoute()
          }
          assignmentId={assignmentId}
          assessmentId={assessmentId}
          shouldShowViewInstructions={true}
        />
      ) : null;

    case "IMAGEVIEWER":
      return (
        <ImageViewerV2
          key={mediaDataAttachment.id}
          attachment={mediaDataAttachment}
          getRef={ref => {
            imageViewerRef.current = ref;
          }}
          imageContainerStyles={{ maxHeight: "100%" }}
        />
      );

    case "FILEVIEWER":
      return (
        <FileViewer
          file={mediaDataAttachment}
          key={mediaDataAttachment.id}
          mode={_.get(props, "mode", "preview")}
        />
      );

    case "PDFTRONVIEWER":
      if (preLoadPdfTron) return null;

      return (
        <PdfTronViewerV3
          file={mediaDataAttachment}
          key={mediaDataAttachment.id}
          shouldShowGiveFeedbackButton={shouldShowGiveFeedbackButton}
          onPdfTronSwitchToEditMode={onPdfTronSwitchToEditMode}
          studentDetails={studentDetails}
          pdftronViewerMode={pdftronViewerMode}
          annotationUserInfo={userInfo}
          onDiscardDirectly={onDiscardDirectly}
          onUpdateSuccessfully={onPdftronUpdateSuccessfullly}
          fullScreenButtonStatus={fullScreenButtonStatus}
          showSaveButton={showSaveButton}
          xfdfString={xfdfString}
          setPdftronInstance={setPdftronInstance}
        />
      );
    case "MEDIAPLACEHOLDER":
    case "MIRCOSOFT_URL":
      return (
        <div
          key={mediaDataAttachment?.id}
          className={classes.mediaPlaceWrapper}
          style={{
            alignItems:
              _.get(mediaDataAttachment, "type") === "NOTE" ? "center" : "",
          }}
        >
          {showOpenInNewTabButton ? (
            <div className={classes.openNewTabViewContainer}>
              <div className={classes.iconCardMediaPlaceholderContainer}>
                <MediaPlaceholder
                  attachments={[mediaDataAttachment]}
                  imageMaxHeight={176}
                  imageMaxWidth={176}
                  sourceWidth={176}
                  sourceHeight={176}
                  containerStyle={styles.mediaPlaceHolderContainerStyle}
                  itemStyles={styles.mediaPlaceHolderItemStyle}
                  showIconCardsWithExtension={true}
                  showMiddleContainerIcons={false}
                  showVideoPlayer={false}
                  onClickMedia={onClickToOpenNewTab}
                  scalableRatio={1}
                />
              </div>
              <Button
                variant="primary"
                size="small"
                testId={`${testId}-theatreMode-openInNewTab`}
                icon={<ArrowOutOutlined />}
                style={{ marginTop: "16px" }}
                onClick={onClickToOpenNewTab}
              >
                {t("common:open_in_new_tab")}
              </Button>
              {/* Note:not needed for now keeping it for if required later */}
              {/* <div className="text-body-3 text-textOn mt-2">
                {t("common:leave_message_for_open_in_new_tab")}
              </div> */}
            </div>
          ) : (
            <MediaPlaceholder
              key={mediaDataAttachment?.id}
              attachments={[mediaDataAttachment]}
              imageMaxHeight={780}
              imageMaxWidth={1080}
              showVideoPlayer={true}
              isTheatreMode={true}
              showFullscreenImage={true}
              containerStyle={
                _.get(mediaDataAttachment, "type") === "NOTE"
                  ? styles.mediaPlaceHolderNoteContainerStyle
                  : styles.mediaPlaceHolderContainerStyle
              }
              noteContainerStyle={styles.noteContainerStyle}
              noteContainerWithLimitStyles={styles.noteContainerWithLimitStyles}
              noteLinesContainerStyles={styles.noteLinesContainerStyles}
              itemStyles={styles.itemStyles}
              showLinkInNewTab={true}
              showVideoPlayerV2={true}
              videoPlaybackTime={currentVideoPlaybackTime}
              audioPlaybackTime={currentAudioPlaybackTime}
              videoPlayerRef={videoPlayerRef}
              audioPlayerRef={audioPlayerRef}
              showAudioDownloadButton={false}
            />
          )}
        </div>
      );
    case "QUESTION":
    case "QUESTION_SUBMISSION":
      return (
        <div className={classes.openNewTabViewContainer}>
          <div className={classes.iconCardMediaPlaceholderContainer}>
            <MediaPlaceholder
              attachments={[mediaDataAttachment]}
              imageMaxHeight={176}
              imageMaxWidth={176}
              sourceWidth={176}
              sourceHeight={176}
              containerStyle={styles.mediaPlaceHolderContainerStyle}
              itemStyles={{
                width: "176px",
                height: "176px",
                borderRadius: "8px",
              }}
              showIconCardsWithExtension={true}
              showMiddleContainerIcons={false}
              showVideoPlayer={false}
              onClickMedia={() => setShowAttachmentEditor(true)}
              scalableRatio={1}
            />
          </div>
          <Button
            variant="primary"
            testId={`${testId}-theatreMode-openWorksheet`}
            size="small"
            style={{ marginTop: "16px" }}
            onClick={() => setShowAttachmentEditor(true)}
          >
            {t("common:open_with_label", { label: t("common:worksheet") })}
          </Button>
        </div>
      );

    case "ASSESSMENT_EVALUATION_TOOL_VIEWER":
      return showRevampedEvaluationUI ? (
        <GrouppedAssessmentToolsViewerV2
          {...props}
          assessmentTools={assessmentTools}
        />
      ) : (
        <GrouppedAssessmentToolsViewer
          {...props}
          assessmentTools={assessmentTools}
        />
      );

    case "POST_RATED_LEARNING_GOALS":
      return (
        <PostRateonLearningGoals
          {...postRatedLearningGoalsDetails}
          students={ratedStudents}
          mode={"preview"}
          showLearningGoalsTheatreModePreview={true}
          showLearningGoalsPreview={true}
        />
      );
    default:
      return (
        <div className={classes.noPreviewContainer}>
          <EmptyState
            title={props?.t("common:no_preview_available")}
            subtitle={props?.t("common:no_media_has_been_attached_to_preview")}
            illustration={EvidenceIllustration}
            size={"small"}
          />
        </div>
      );
  }
});

const mapActionCreators = {
  getStudentAssignmentMicrosoftLink,
};

const mapStateToProps = (state, ownProps) => {
  return {};
};

export default compose(
  I18nHOC({ resource: ["common", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators)
)(ViewerComponent);
ViewerComponent.displayName = "ViewerComponent";
